import { useEffect, useState } from "react";
import RightModal from "../components/templates/RightModal";
import { getProductCategories, getProductDetails, getProductsList, getReverseAccessories, resetProductDetails, resetRelatedProducts, selectProductCategories, selectProductCategoriesLoading, selectProductsData, selectProductsLoading } from "../redux/products/productsSlice";
import { getSuppliersAll, selectSuppliersAll, selectSuppliersAllLoading } from "../redux/suppliers/suppliersSlice";
import DynamicIcon from "../components/DynamicIcon";
import ProductLine from "../components/productsPage/ProductLine";
import ListPagination from "../components/templates/ListPagination";
import LoadingSection from "../components/LoadingSection";
import { selectAccountData } from "../redux/main/accountSlice";
import { resetRefreshNow, selectRightModalId, selectRightModalIsOpen, selectRightModalIsRefreshNow, selectRightModalmode, setModal } from "../redux/rightModalSlice";
import { useDispatch, useSelector } from "react-redux";
import DynamicTitle from "../components/DynamicTitle";
import AddEditProduct from "../components/productsPage/AddEditProduct";
import ViewProduct from "../components/productsPage/ViewProduct";
import ProductPreview from "../components/productsPage/ProductPreview";

export default function ProductsList() {
    const dispatch = useDispatch();
    const productsList = useSelector(selectProductsData);
    const productsLoading = useSelector(selectProductsLoading);
    const productCategories = useSelector(selectProductCategories);
    const productCategoriesLoading = useSelector(selectProductCategoriesLoading);
    const suppliersAll = useSelector(selectSuppliersAll);
    const suppliersAllLoading = useSelector(selectSuppliersAllLoading);
    const accountData = useSelector(selectAccountData);
    const token = localStorage.getItem('installamelo-auth-token');
    const [searchTerm, setSearchTerm] = useState('');
    const [categoryFilter, setCategoryFilter] = useState('');
    const [supplierFilter, setSupplierFilter] = useState('');
    
    // RightModal gestita tramite Redux
    const rightModalIsOpen = useSelector(selectRightModalIsOpen);
    const rightModalMode = useSelector(selectRightModalmode);
    const rightModalProductId = useSelector(selectRightModalId);
    
    // Carico le categorie di prodotti e i fornitori quando il componente viene montato
    useEffect(() => {
        if (accountData?._id) {
            if (!productCategories?.length) {
                dispatch(getProductCategories({
                    accountId: accountData._id,
                    token
                }));
            }
            if (!suppliersAll?.length) {
                dispatch(getSuppliersAll({
                    accountId: accountData._id,
                    token
                }));
            }
        }
    }, [dispatch, accountData, productCategories?.length, suppliersAll?.length]);
    
    // Funzione che verrà passata al componente ListPagination
    const handlePageUpdate = (start, length) => {
        dispatch(getProductsList({
            accountId: accountData?._id,
            token,
            search: searchTerm.length >= 3 ? searchTerm : '',
            start,
            length,
            categoryId: categoryFilter,
            supplierId: supplierFilter
        }));
        dispatch(resetRefreshNow());
    };

    // Eseguo la chiamata iniziale quando il componente viene montato o quando cambiano i filtri
    useEffect(() => {
        if (accountData?._id) {
            dispatch(getProductsList({
                accountId: accountData._id,
                token,
                search: searchTerm.length >= 3 ? searchTerm : '',
                start: 0,
                length: 10,
                categoryId: categoryFilter,
                supplierId: supplierFilter
            }));
            dispatch(resetRefreshNow());
        }
    }, [dispatch, accountData, searchTerm, categoryFilter, supplierFilter]);
    
    // Carica i dettagli del prodotto quando il modale viene aperto per modifica, visualizzazione o preview
    useEffect(() => {
        if (rightModalIsOpen && rightModalProductId && (rightModalMode === 'edit' || rightModalMode === 'view' || rightModalMode === 'preview')) {
            dispatch(getProductDetails({
                productId: rightModalProductId,
                token
            }));
            // In modalita' preview carico i prodotti correlati (ricerca inversa)
            if (rightModalMode === 'preview') {
                dispatch(getReverseAccessories({
                    productId: rightModalProductId,
                    token
                }));
            }
        }
    }, [rightModalIsOpen, rightModalProductId, rightModalMode, dispatch, token]);

    // Reset dei dettagli prodotto e prodotti correlati quando si chiude il modale
    useEffect(() => {
        if (!rightModalIsOpen) {
            dispatch(resetProductDetails());
            dispatch(resetRelatedProducts());
        }
    }, [rightModalIsOpen, dispatch]);

    // Avvio il refresh dei dati quando isRefreshNow della RightModal si attiva
    const rightModalIsRefreshNow = useSelector(selectRightModalIsRefreshNow);
    useEffect(() => {
        if (rightModalIsRefreshNow) handlePageUpdate(0, 10);
    }, [rightModalIsRefreshNow]);
    
    // Renderizza le opzioni del select categorie con indentazione gerarchica
    const renderCategoryOptions = (categories, level) => {
        const options = [];
        categories.forEach((cat) => {
            const prefix = '\u00A0\u00A0'.repeat(level);
            const dash = level > 0 ? '└ ' : '';
            options.push(
                <option key={cat._id} value={cat._id}>
                    {prefix}{dash}{cat.name}
                </option>
            );
            if (cat.children && cat.children.length > 0) {
                options.push(...renderCategoryOptions(cat.children, level + 1));
            }
        });
        return options;
    };

    return (
        <div className="page">
            {productsLoading && <LoadingSection isFixed />}
            <DynamicTitle title="Prodotti" />
            
            <h1>Prodotti</h1>
            <div className="box">
                {/* Barra di ricerca e filtri */}
                <div className="flex flex-wrap justify-between items-center gap-2 mb-4">
                    <div className="relative w-96">
                        <input
                            type="text"
                            className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full pe-10 p-2.5"
                            placeholder="Cerca prodotto..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                        <div className="absolute inset-y-0 end-0 flex items-center pe-3">
                            {searchTerm ? (
                                <button onClick={() => setSearchTerm('')}>
                                    <DynamicIcon iconClass="fa-solid fa-times text-gray-400 hover:text-gray-600" />
                                </button>
                            ) : (
                                <DynamicIcon iconClass="fa-solid fa-search text-gray-400 pointer-events-none" />
                            )}
                        </div>
                    </div>
                    
                    {/* Filtri */}
                    <div className="flex flex-wrap gap-2">
                        {/* Filtro per Categoria (albero gerarchico) */}
                        <select
                            className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 p-2.5"
                            value={categoryFilter}
                            onChange={(e) => setCategoryFilter(e.target.value)}
                            disabled={productCategoriesLoading}
                        >
                            <option value="">Tutte le categorie</option>
                            {productCategories && renderCategoryOptions(productCategories, 0)}
                        </select>

                        {/* Filtro per Fornitore */}
                        <select
                            className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 p-2.5"
                            value={supplierFilter}
                            onChange={(e) => setSupplierFilter(e.target.value)}
                            disabled={suppliersAllLoading}
                        >
                            <option value="">Tutti i fornitori</option>
                            {suppliersAll && suppliersAll.map((supplier) => (
                                <option key={supplier._id} value={supplier._id}>
                                    {supplier.company || supplier._id}
                                </option>
                            ))}
                        </select>

                        {/* Bottone Nuovo Prodotto */}
                        <button
                            className="primary"
                            onClick={() => dispatch(setModal({ mode: 'add' }))}
                        >
                            <DynamicIcon iconClass="fa-solid fa-plus" />
                            Nuovo Prodotto
                        </button>
                    </div>
                </div>

                <ListPagination
                    initTotalElements={productsList?.recordsFiltered || 0}
                    onPageChange={handlePageUpdate}
                >
                    {/* Tabella prodotti */}
                    <div className="w-full text-sm flex flex-col gap-2">
                        {/* Headers */}
                        <div className="w-full flex justify-between font-bold">
                            <div className="px-4 py-4 w-[10%]">CODICE</div>
                            <div className="px-4 py-4 w-[18%]">DESCRIZIONE</div>
                            <div className="px-4 py-4 w-[12%]">CATEGORIA</div>
                            <div className="px-4 py-4 w-[12%]">FORNITORE</div>
                            <div className="px-4 py-4 w-[10%] text-right">ACQUISTO</div>
                            <div className="px-4 py-4 w-[6%] text-right">MARKUP</div>
                            <div className="px-4 py-4 w-[10%] text-right">VENDITA</div>
                            <div className="px-4 py-4 w-[8%] text-right">QTA'</div>
                            <div className="px-4 py-4 w-[14%] flex justify-end">AZIONI</div>
                        </div>
                        
                        {/* Rows */}
                        {
                            productsList?.data?.map((product, index) => (
                                <ProductLine
                                    key={product._id || product.id}
                                    productData={product}
                                    index={index}
                                />
                            ))
                        }
                        
                        {/* Messaggio quando non ci sono dati */}
                        {(!productsList?.data || productsList.data.length === 0) && !productsLoading && (
                            <div className="w-full py-16 flex flex-col items-center justify-center" key="no-data-message">
                                <DynamicIcon iconClass="fa-solid fa-box-open text-5xl text-gray-300 mb-4" />
                                <p className="text-gray-500 font-medium mb-1">Nessun prodotto trovato</p>
                                <p className="text-gray-400 text-sm mb-4">
                                    {searchTerm || categoryFilter || supplierFilter
                                        ? "Prova a modificare i filtri di ricerca"
                                        : "Inizia aggiungendo il tuo primo prodotto"}
                                </p>
                                {!searchTerm && !categoryFilter && !supplierFilter && (
                                    <button
                                        className="primary"
                                        onClick={() => dispatch(setModal({ mode: 'add' }))}
                                    >
                                        <DynamicIcon iconClass="fa-solid fa-plus" />
                                        Nuovo Prodotto
                                    </button>
                                )}
                            </div>
                        )}
                    </div>
                </ListPagination>
            </div>

            {/* RIGHT MODAL per inserimento o modifica prodotto */}
            {
                rightModalIsOpen && (
                    <RightModal>
                        {(rightModalMode === 'add' || rightModalMode === 'edit') && (
                            <AddEditProduct mode={rightModalMode} />
                        )}
                        {
                            rightModalMode === 'view' && rightModalProductId && (
                                <ViewProduct />
                            )
                        }
                        {
                            rightModalMode === 'preview' && rightModalProductId && (
                                <ProductPreview />
                            )
                        }
                    </RightModal>
                )
            }
        </div>
    );
}
